import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

import {
  RQ_DELETE_PRODUCT_API,
  RQ_GET_ALL_PRODUCT_API,
  RQ_GET_PRODUCT_API,
  RQ_NEW_ARRIVALS_PRODUCTS__API,
  RQ_PRODUCTS_BY_FILTER_API,
  RQ_PRODUCTS_BY_IDS_API,
  RQ_PRODUCT_IMAGE_UPLOAD_API,
  RQ_PUT_PRODUCT_API,
  RQ_TRENDING_PRODUCTS__API,
} from './constants';
import {rqCreateRequest} from './rqHttpUtils';

export const RqGetAllProducts =() => {
  return useQuery({
    queryKey: ['rq_get_all_products'],
    queryFn: async () => {
      const response = await rqCreateRequest(RQ_GET_ALL_PRODUCT_API, null, null);
      return response || [];
    },
    refetchOnWindowFocus: false,
    retry: 1,
  });
}; 

export const RqGetProductsByIds = (ids, options = {}) => {
  const stableIds = ids?.slice().sort(); 

  return useQuery({
    queryKey: ['rq_get_products_by_ids', stableIds?.join(',')],
    queryFn: async () => {
      if (!stableIds?.length) {return [];}

      return rqCreateRequest(
        RQ_PRODUCTS_BY_IDS_API,
        {ids: stableIds},
        null,
        null,
      );
    },
    enabled: !!stableIds?.length,
    staleTime: 0,          
    cacheTime: 0,          
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    ...options,
  });
};

//show all product also inactive product
export const RqGetProducts =() => {
  return useQuery({
    queryKey: ['rq_get_products'],
    queryFn: async () => {
      const response = await rqCreateRequest(RQ_GET_PRODUCT_API, null, null);
      return response || [];
    },
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

export const RqUpdateProduct = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['rq_update_product_allfields'],

    mutationFn: async ({token, formData, id}) => {
      if (!token) {throw new Error('Auth token missing');}

      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const url = `${baseUrl}/api/product/${id}/update/allfields`;

      const res = await fetch(url, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!res.ok) {
        const text = await res.text();
        console.error('Backend update error:', text);
        throw new Error(text || 'Update failed');
      }

      return res.json();
    },
    onSuccess: (data, variables) => {
      const {id} = variables;

      queryClient.invalidateQueries({queryKey: ['rq_get_product', id]});

      queryClient.invalidateQueries({queryKey: ['rq_get_all_products']});
      queryClient.invalidateQueries({queryKey: ['rq_get_products']});
      queryClient.invalidateQueries({queryKey: ['rq_trending_products']});
      queryClient.invalidateQueries({queryKey: ['rq_new_arrivals_products']});

      options?.onSuccess?.(data, variables);
    },

    ...options,
  });
};

export const RqAddProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['rq_add_product'],
    mutationFn: async ({productData, token}) => {
      if (!token) {throw new Error('Auth token missing');}

      return rqCreateRequest(
        RQ_PUT_PRODUCT_API,
        productData,
        token,
        null,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['rq_get_all_products']});
      queryClient.invalidateQueries({queryKey: ['rq_get_products']});
      queryClient.invalidateQueries({queryKey: ['rq_trending_products']});
      queryClient.invalidateQueries({queryKey: ['rq_new_arrivals_products']});
    },

  });
};

export const RqAddProductFormData = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['rq_add_product_formdata'],

    mutationFn: async ({token, formData}) => {
      if (!token) {throw new Error('Auth token missing');}

      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const url = `${baseUrl}/api/product/register`;

      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      // ✅ Proper error handling
      if (!res.ok) {
        let errorMessage = 'Create product failed';

        try {
          const errorData = await res.json(); // parse JSON
          errorMessage = errorData?.message || errorMessage;
        } catch {
          // fallback if response is not JSON
          const text = await res.text();
          errorMessage = text || errorMessage;
        }

        throw new Error(errorMessage);
      }

      return res.json();
    },

    onSuccess: () => {
      // ✅ Refresh data
      queryClient.invalidateQueries({queryKey: ['rq_get_all_products']});
      queryClient.invalidateQueries({queryKey: ['rq_get_products']});
      queryClient.invalidateQueries({queryKey: ['rq_trending_products']});
      queryClient.invalidateQueries({queryKey: ['rq_new_arrivals_products']});
    },

    ...options,
  });
};
export const RQGetProductsByFilter = (filters, options = {}) => {
  return useQuery({
    queryKey: ['rq_get_products_by_filter', filters],

    queryFn: async ({queryKey, signal}) => {
      const filtersData = queryKey[1];

      if (!filtersData || Object.keys(filtersData).length === 0) {
        return [];
      }

      const response = await rqCreateRequest(
        RQ_PRODUCTS_BY_FILTER_API,
        filtersData, 
        null,
        null,
        signal,
      );

      return response;
    },

    enabled: Object.keys(filters || {}).length > 0,
    keepPreviousData: true,
    ...options,
  });
};

export const RqGetTrendingProducts = (options = {}) => {
  return useQuery({
    queryKey: ['rq_trending_products'],
    queryFn: async () => {
      const response = await rqCreateRequest(
        RQ_TRENDING_PRODUCTS__API,
        null,
        null,
      );
      return Array.isArray(response) ? response : [];
    },
    refetchOnWindowFocus: false,
    retry: 1,
    ...options,
  });
};

export const RqGetNewArrivalsProducts = (options = {}) => {
  return useQuery({
    queryKey: ['rq_new_arrivals_products'],
    queryFn: async () => {
      const response = await rqCreateRequest(
        RQ_NEW_ARRIVALS_PRODUCTS__API,
        null,
        null,
      );

      if (response && typeof response === 'object') {
        return Object.values(response).flat();
      }

      return [];
    },
    refetchOnWindowFocus: false,
    retry: 1,
    ...options,
  });
};
export const RqUploadProductImage = (options = {}) => {
  return useMutation({
    mutationKey: ['rq_upload_product_image'],
    mutationFn: async ({file, token, productId, variantColor}) => {
      if (!token) {throw new Error('Auth token missing');}

      return rqCreateRequest(
        RQ_PRODUCT_IMAGE_UPLOAD_API,
        {
          productId,
          variantColor,
          fileName: file.name,
          contentType: file.type,
        },
        token,
        {
          'Content-Type': 'application/json',
        },
      );
    },
    ...options,
  });
};

export const RqDeleteProduct = (options = {}) => {  
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['rq_delete_product'],
    mutationFn: async ({id, token}) => {
      if (!token) {
        throw new Error('Auth token missing');
      }

      return rqCreateRequest(
        RQ_DELETE_PRODUCT_API,
        null,
        token,
        {id}, 
      );
    },
    onSuccess: () => {  
      queryClient.invalidateQueries({queryKey: ['rq_get_all_products']});
      queryClient.invalidateQueries({queryKey: ['rq_get_products']});
      queryClient.invalidateQueries({queryKey: ['rq_trending_products']});
      queryClient.invalidateQueries({queryKey: ['rq_new_arrivals_products']});
    },
    ...options,
  });  
};
